import React, { useEffect, useRef, useCallback } from 'react';

interface DunningModalProps {
  isOpen: boolean;
  onClose: () => void;
  reasonings?: string[];
}

const DunningModal: React.FC<DunningModalProps> = ({
  isOpen,
  onClose,
  reasonings = []
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  // Handle Escape key
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Focus trap functionality
  const handleTabKey = useCallback((event: KeyboardEvent) => {
    if (event.key !== 'Tab') return;

    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    }
  }, []);

  // Set up event listeners and focus management
  useEffect(() => {
    if (!isOpen) return;

    // Store the previously focused element
    previouslyFocusedElement.current = document.activeElement as HTMLElement;

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleTabKey);

    // Focus the close button initially
    const timer = setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleTabKey);
      clearTimeout(timer);
      
      // Restore focus to previously focused element
      if (previouslyFocusedElement.current) {
        previouslyFocusedElement.current.focus();
      }
    };
  }, [isOpen, handleKeyDown, handleTabKey]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black opacity-30" 
        onClick={onClose}
        aria-hidden="true"
      />
      <div 
        ref={modalRef}
        className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dunning-modal-title"
        aria-describedby="dunning-modal-content"
        tabIndex={-1}
      >
        <div className="px-6 py-4 border-b">
          <h3 
            id="dunning-modal-title"
            className="text-lg font-medium text-gray-900"
          >
            Dunning Analysis
          </h3>
        </div>
        <div 
          id="dunning-modal-content"
          className="px-6 py-4 max-h-96 overflow-auto"
        >
          {reasonings && reasonings.length > 0 ? (
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
              {reasonings.map((r, idx) => (
                <li key={idx}>{r}</li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-500">No dunning analysis available.</div>
          )}
        </div>
        <div className="px-6 py-4 border-t flex justify-end">
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            aria-label="Close dunning analysis modal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DunningModal;
